import { useState } from "react";
import { ShieldAlert } from "lucide-react";
import { api, safe } from "@/lib/api";
import { toast } from "sonner";

const DISP = { Pending: "bg-amber-50 text-amber-700 ring-amber-200", "False positive": "bg-emerald-50 text-emerald-700 ring-emerald-200", "True match": "bg-red-50 text-red-700 ring-red-200" };

function Match({ m, c, onCase }) {
  const [reason, setReason] = useState("");
  const decide = safe(async (disposition) => {
    const { data } = await api.put(`/kyc/cases/${c.id}/screening/${m.id}`, { disposition, reason });
    onCase(data);
    setReason("");
    toast.success(`Match marked ${disposition.toLowerCase()}`);
  });
  const open = m.disposition === "Pending" && !c.signoff;
  return (
    <div data-testid={`kyc-match-${m.id}`} className="px-5 py-4 space-y-2">
      <div className="flex flex-wrap justify-between gap-2">
        <div className="text-xs"><span className="font-semibold text-slate-900">{m.screened_name}</span><span className="text-slate-400"> vs </span><span className="font-semibold text-slate-900">{m.matched_name}</span></div>
        <div className="flex items-center gap-2">
          <span className="chip border-slate-200 bg-slate-50 text-slate-700">{m.list} · {m.program}</span>
          <span className="num text-[11px] text-slate-700" data-testid={`kyc-match-score-${m.id}`}>{m.score}% match</span>
          <span data-testid={`kyc-match-disposition-${m.id}`} className={`chip ring-1 border-0 ${DISP[m.disposition]}`}>{m.disposition}</span>
        </div>
      </div>
      <div className="text-[11px] text-slate-500">{m.detail}</div>
      {m.by && <div className="text-[11px] text-slate-600">{m.by}: <span className="italic">{m.reason}</span></div>}
      {open && (
        <div className="flex flex-wrap gap-2 pt-1">
          <input data-testid={`kyc-match-reason-${m.id}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Disposition reason (DOB, nationality, address evidence…)"
            className="flex-1 min-w-[240px] text-xs px-3 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-sky-400" />
          <button data-testid={`kyc-match-fp-${m.id}`} disabled={reason.trim().length < 10} onClick={() => decide("False positive")} className="btn-ghost disabled:opacity-40">False positive</button>
          <button data-testid={`kyc-match-true-${m.id}`} disabled={reason.trim().length < 10} onClick={() => decide("True match")} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40 transition-colors">True match</button>
        </div>
      )}
    </div>
  );
}

export function KycScreening({ c, onCase }) {
  return (
    <div className="panel overflow-hidden" data-testid="kyc-screening">
      <div className="px-5 py-3.5 border-b border-slate-100 flex justify-between items-center">
        <span className="text-sm font-semibold text-slate-900 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-sky-600" />OFAC & name-screening matches</span>
        <span className="num text-[11px] text-slate-500">{c.progress.matches_pending} of {c.progress.matches_total} pending</span>
      </div>
      <div className="divide-y divide-slate-100">
        {c.screening.map((m) => <Match key={m.id} m={m} c={c} onCase={onCase} />)}
        {!c.screening.length && <div className="px-5 py-4 text-xs text-slate-500" data-testid="kyc-no-matches">No potential matches across OFAC SDN, consolidated, PEP or adverse-media lists.</div>}
      </div>
    </div>
  );
}
